import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinner } from 'primeng/progressspinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { CONSTANTS } from '../../constants/constants';
import { UserService } from '../../../core/services/user.service';
import { BureauService } from '../../../core/services/bureau.service';
import {
  BureauAddressDto,
  CurrentUserBureauDto,
  CurrentUserResponseDto,
  UpdateCurrentUserBureauDto,
  UpdateCurrentUserRequestDto,
  UserDto,
} from '../../../core/models/user-dto.model';
import { BureauType } from '../../enums/bureau-type.enum';
import { UserRole } from '../../enums/user-role.enum';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, finalize, map, take } from 'rxjs/operators';

export type ProfileSettingsSection =
  | 'personal'
  | 'firm'
  | 'professional'
  | 'ecase'
  | 'notifications'
  | 'security'
  | 'team'
  | 'subscription'
  | 'delete';

interface SidebarItem {
  id: ProfileSettingsSection;
  icon: string;
  labelKey: string;
}

interface TeamMemberCard {
  id: string;
  initials: string;
  displayName: string;
  title: string;
  email: string;
  expertise: string[];
}

@Component({
  selector: 'app-profile-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    AvatarModule,
    InputTextModule,
    ProgressSpinner,
    TranslateModule,
  ],
  templateUrl: './profile-modal.component.html',
  styleUrl: './profile-modal.component.css',
})
export class ProfileModalComponent implements OnChanges {
  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncRoleLabel();
      this.rebuildTeamCards();
    });
  }
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  readonly CONSTANTS = CONSTANTS;
  activeSection: ProfileSettingsSection = 'personal';

  personalSectionLoading = false;
  personalSaving = false;
  firmSaving = false;
  teamSectionLoading = false;
  /** Shown on firm tab while `GET /api/v1/bureau` runs (lazy; not on dialog open). */
  firmBureauSectionLoading = false;

  /** Last merged profile for lazy bureau merge; reset on dialog close. */
  private profileResponseCache: CurrentUserResponseDto | null = null;
  /** After first successful lazy bureau fetch this session, skip repeat GET. */
  private bureauTabEndpointFetched = false;

  get settingsSidebarItems(): SidebarItem[] {
    const afterPersonal: SidebarItem[] = this.showFirmSection
      ? [{ id: 'firm', icon: 'pi pi-home', labelKey: CONSTANTS.PROFILE_SETTINGS_SECTION_FIRM }]
      : [];
    return [
      { id: 'personal', icon: 'pi pi-user', labelKey: CONSTANTS.PROFILE_SETTINGS_SECTION_PERSONAL },
      ...afterPersonal,
      { id: 'professional', icon: 'pi pi-briefcase', labelKey: CONSTANTS.PROFILE_SETTINGS_SECTION_PROFESSIONAL },
      { id: 'ecase', icon: 'pi pi-building', labelKey: CONSTANTS.PROFILE_SETTINGS_SECTION_ECASE },
      { id: 'notifications', icon: 'pi pi-bell', labelKey: CONSTANTS.PROFILE_SETTINGS_SECTION_NOTIFICATIONS },
      { id: 'security', icon: 'pi pi-shield', labelKey: CONSTANTS.PROFILE_SETTINGS_SECTION_SECURITY },
      { id: 'team', icon: 'pi pi-users', labelKey: CONSTANTS.PROFILE_SETTINGS_SECTION_TEAM },
      { id: 'subscription', icon: 'pi pi-credit-card', labelKey: CONSTANTS.PROFILE_SETTINGS_SECTION_SUBSCRIPTION },
      { id: 'delete', icon: 'pi pi-trash', labelKey: CONSTANTS.PROFILE_SETTINGS_SECTION_DELETE },
    ];
  }

  personalForm = {
    firstName: '',
    lastName: '',
    email: '',
    position: '',
  };

  /** Resolved from `bureau.bureauType`; unknown/missing → both firm block and solo bar row hidden. */
  private resolvedBureauType: BureauType | null = null;

  /** Sent as `bureauUIC` on save; never mixed with bar/EGN display. */
  bureauUicForSave = '';

  /** Solo practice: single editable line → split into `attorneyNumber` / `egn` on save. */
  soloBarEgnCombined = '';
  /** Solo: bureau name from API — resent on PUT so backend does not clear bureau. */
  private soloBureauNameForSave = '';

  /** Bureau-type firm card (read-only). */
  firmLogoInitials = '—';
  firmName = '';
  firmUicDisplay = '';
  firmBarNumberDisplay = '';
  firmAddressLine = '';
  firmRepresentatives = '';

  avatarInitials = '—';
  /** Remote URL from API or cleared locally when user removes photo. */
  avatarImageUrl: string | null = null;

  /** Raw `user.role` from API; label shown in form is translated via `syncRoleLabel`. */
  private roleRaw: string | null = null;

  private userId = '';
  private usernameFromApi = '';
  private userStatusFromApi = '';

  teamMembers: TeamMemberCard[] = [];

  private teamUsersSource: UserDto[] = [];
  /** True after successful `/api/v1/users` fetch this modal session; reset on close. */
  private teamUsersFetched = false;

  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly userService = inject(UserService);
  private readonly bureauService = inject(BureauService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.loadUserProfile();
    }
  }

  selectSection(id: ProfileSettingsSection): void {
    this.activeSection = id;
    if (id === 'team' && !this.teamUsersFetched) {
      this.loadTeamUsers();
    }
    if (id === 'firm') {
      this.ensureFirmBureauLoadedFromApi();
    }
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.activeSection = 'personal';
    this.userId = '';
    this.usernameFromApi = '';
    this.userStatusFromApi = '';
    this.resolvedBureauType = null;
    this.bureauUicForSave = '';
    this.soloBarEgnCombined = '';
    this.soloBureauNameForSave = '';
    this.firmLogoInitials = '—';
    this.firmName = '';
    this.firmUicDisplay = '';
    this.firmBarNumberDisplay = '';
    this.firmAddressLine = '';
    this.firmRepresentatives = '';
    this.teamUsersFetched = false;
    this.teamUsersSource = [];
    this.teamMembers = [];
    this.teamSectionLoading = false;
    this.firmSaving = false;
    this.firmBureauSectionLoading = false;
    this.profileResponseCache = null;
    this.bureauTabEndpointFetched = false;
  }

  get showFirmSection(): boolean {
    return this.resolvedBureauType === BureauType.BUREAU;
  }

  get canEditFirmSection(): boolean {
    return this.showFirmSection && this.isOfficeManager;
  }


  get showFirmBarNumberField(): boolean {
    return this.resolvedBureauType !== BureauType.BUREAU;
  }

  get isOfficeManager(): boolean {
    const r = this.roleRaw;
    return r === UserRole.OFFICE_MANAGER || r === 'ROLE_OFFICE_MANAGER';
  }

  get showSoloBarEgnField(): boolean {
    return this.resolvedBureauType === BureauType.SOLO_PRACTICE;
  }

  /** Position (role label) not shown for solo practice — redundant with solo context. */
  get showPositionField(): boolean {
    return this.resolvedBureauType !== BureauType.SOLO_PRACTICE;
  }

  /** Read-only placeholder when bureau string field empty. */
  get firmRepresentativesDisplay(): string {
    return this.firmRepresentatives || '—';
  }

  savePersonal(): void {
    if (this.personalSaving || this.personalSectionLoading) {
      return;
    }
    this.personalSaving = true;
    const userBody = this.buildPersonalUserSavePayload();
    const save$ =
      this.resolvedBureauType === BureauType.SOLO_PRACTICE
        ? this.userService.saveCurrentUserProfile(userBody).pipe(
            concatMap(() => this.bureauService.updateCurrentBureau(this.buildSoloBureauUpdatePayload())),
          )
        : this.userService.saveCurrentUserProfile(userBody);

    save$
      .pipe(
        take(1),
        catchError(() => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant(CONSTANTS.PROFILE_SETTINGS_SAVE_FAILED),
            detail: '',
          });
          return EMPTY;
        }),
        finalize(() => {
          this.personalSaving = false;
        }),
      )
      .subscribe((res) => {
        if (this.resolvedBureauType !== BureauType.SOLO_PRACTICE && this.isFullProfileResponse(res)) {
          this.applyUserProfile(res);
        } else {
          this.refreshProfileFromServer();
        }
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(CONSTANTS.PROFILE_SETTINGS_SAVE_SUCCESS),
          detail: '',
        });
      });
  }

  /** Persists firm block via `PUT /api/v1/bureau`. */
  saveFirm(): void {
    if (
      !this.canEditFirmSection ||
      this.firmSaving ||
      this.personalSectionLoading ||
      this.firmBureauSectionLoading
    ) {
      return;
    }
    this.firmSaving = true;
    const bureauBody = this.buildFirmBureauUpdatePayload();
    this.bureauService
      .updateCurrentBureau(bureauBody)
      .pipe(
        take(1),
        catchError(() => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant(CONSTANTS.PROFILE_SETTINGS_SAVE_FAILED),
            detail: '',
          });
          return EMPTY;
        }),
        finalize(() => {
          this.firmSaving = false;
        }),
      )
      .subscribe((res) => {
        if (this.isFullProfileResponse(res)) {
          this.applyUserProfile(res);
        } else {
          this.refreshProfileFromServer();
        }
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(CONSTANTS.PROFILE_SETTINGS_SAVE_SUCCESS),
          detail: '',
        });
      });
  }

  /** User slice only; solo bureau fields go through `PUT /api/v1/bureau`. */
  private buildPersonalUserSavePayload(): UpdateCurrentUserRequestDto {
    const uic =
      this.canEditFirmSection ? this.firmUicDisplay.trim() : this.bureauUicForSave.trim();
    return {
      id: this.userId,
      firstName: this.personalForm.firstName.trim(),
      lastName: this.personalForm.lastName.trim(),
      email: this.personalForm.email.trim(),
      username: this.usernameFromApi,
      bureauUIC: uic,
      role: this.roleRaw ?? '',
      status: this.userStatusFromApi,
    };
  }

  private buildSoloBureauUpdatePayload(): UpdateCurrentUserBureauDto {
    const uic = this.bureauUicForSave.trim();
    const { attorney, egn } = this.parseSoloBarEgnInput(this.soloBarEgnCombined);
    return {
      name: this.soloBureauNameForSave.trim() || undefined,
      unifiedIdentificationCode: uic || undefined,
      attorneyNumber: attorney || null,
      egn: egn || null,
    };
  }

  private buildFirmBureauUpdatePayload(): UpdateCurrentUserBureauDto {
    const line = this.firmAddressLine.trim();
    return {
      name: this.firmName.trim(),
      unifiedIdentificationCode: this.firmUicDisplay.trim(),
      attorneyNumber: this.firmBarNumberDisplay.trim() || null,
      representatives: this.firmRepresentatives.trim() || null,
      address: { address: line },
    };
  }

  private isFullProfileResponse(res: unknown): res is CurrentUserResponseDto {
    return typeof res === 'object' && res !== null && 'user' in res && 'bureau' in res;
  }

  private refreshProfileFromServer(): void {
    this.loadMergedProfile$()
      .pipe(
        take(1),
        catchError(() => of(undefined)),
      )
      .subscribe((profile) => {
        if (profile) {
          this.applyUserProfile(profile);
        }
      });
  }

  private loadMergedProfile$(): Observable<CurrentUserResponseDto> {
    return forkJoin({
      profile: this.userService.getCurrentUserProfile(),
      bureauSlice: this.bureauService.getCurrentBureau().pipe(
        catchError(() => of<CurrentUserBureauDto | null>(null)),
      ),
    }).pipe(map(({ profile, bureauSlice }) => this.mergeProfileAndBureau(profile, bureauSlice)));
  }

  private mergeProfileAndBureau(
    profile: CurrentUserResponseDto,
    bureauSlice: CurrentUserBureauDto | null,
  ): CurrentUserResponseDto {
    return {
      ...profile,
      bureau: bureauSlice ?? profile.bureau ?? {},
    };
  }

  inviteMember(): void {
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant(CONSTANTS.PROFILE_SETTINGS_TEAM_INVITE_TOAST),
      detail: '',
    });
  }

  /* TODO: profile photo upload/remove — restore with template block
  triggerPhotoSelect(input: HTMLInputElement): void {
    input.click();
  }

  onPhotoFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant(CONSTANTS.PROFILE_SETTINGS_PHOTO_HINT),
        detail: '',
      });
      return;
    }
    this.messageService.add({
      severity: 'info',
      summary: file.name,
      detail: '',
    });
  }

  removePhoto(): void {
    this.avatarImageUrl = null;
    this.syncAvatarInitials();
  }
  */

  private loadTeamUsers(): void {
    if (this.teamUsersFetched || this.teamSectionLoading) {
      return;
    }
    this.teamSectionLoading = true;
    this.userService
      .getUsers()
      .pipe(
        take(1),
        catchError(() => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant(CONSTANTS.PROFILE_SETTINGS_TEAM_LOAD_FAILED),
            detail: '',
          });
          this.teamSectionLoading = false;
          return EMPTY;
        }),
      )
      .subscribe((users) => {
        this.teamUsersSource = users ?? [];
        this.rebuildTeamCards();
        this.teamSectionLoading = false;
        this.teamUsersFetched = true;
      });
  }

  private rebuildTeamCards(): void {
    this.teamMembers = this.teamUsersSource.map((u) => this.mapUserToTeamCard(u));
  }

  private mapUserToTeamCard(u: UserDto): TeamMemberCard {
    const first = u.firstName?.trim() ?? '';
    const last = u.lastName?.trim() ?? '';
    let displayName = [first, last].filter(Boolean).join(' ');
    if (!displayName) {
      displayName = u.username?.trim() || u.email?.trim() || '—';
    }
    const initials = this.initialsForTeamMember(first, last, u.username);
    const roleRaw = u.role != null ? String(u.role).trim() : null;
    const key = this.mapApiRoleToI18nKey(roleRaw);
    const title =
      this.resolvedBureauType === BureauType.SOLO_PRACTICE
        ? ''
        : key
          ? this.translate.instant(key)
          : (roleRaw ?? '');
    const rawExpertise = u.specializations ?? u.expertise;
    const expertise = Array.isArray(rawExpertise)
      ? rawExpertise.map((x) => String(x).trim()).filter(Boolean)
      : [];
    return {
      id: u.id,
      initials,
      displayName,
      title,
      email: u.email?.trim() ?? '',
      expertise,
    };
  }

  private initialsForTeamMember(first: string, last: string, username?: string): string {
    if (first && last) {
      return first[0] + last[0];
    }
    if (first || last) {
      const single = first || last;
      return single.length >= 2 ? single.slice(0, 2) : single;
    }
    const u = username?.trim();
    if (u && u.length >= 2) {
      return u.slice(0, 2).toUpperCase();
    }
    return u ? u[0].toUpperCase() : '—';
  }

  private loadUserProfile(): void {
    this.personalSectionLoading = true;
    this.userService
      .getCurrentUserProfile()
      .pipe(
        take(1),
        catchError(() => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant(CONSTANTS.PROFILE_SETTINGS_LOAD_FAILED),
            detail: '',
          });
          return of(undefined);
        }),
      )
      .subscribe((user) => {
        this.personalSectionLoading = false;
        if (user) {
          this.applyUserProfile(user);
        }
      });
  }

  /** `GET /api/v1/bureau` when user opens firm tab; dialog open uses `GET /user` only. */
  private ensureFirmBureauLoadedFromApi(): void {
    if (this.bureauTabEndpointFetched || this.firmBureauSectionLoading || !this.profileResponseCache) {
      return;
    }
    this.firmBureauSectionLoading = true;
    this.bureauService
      .getCurrentBureau()
      .pipe(
        take(1),
        catchError(() => of<CurrentUserBureauDto | null>(null)),
        finalize(() => {
          this.firmBureauSectionLoading = false;
        }),
      )
      .subscribe((bureauSlice) => {
        const merged = this.mergeProfileAndBureau(this.profileResponseCache!, bureauSlice);
        this.profileResponseCache = merged;
        this.applyUserProfile(merged);
        this.bureauTabEndpointFetched = true;
      });
  }

  private applyUserProfile(d: CurrentUserResponseDto): void {
    const u = d.user;
    const b = d.bureau;
    this.userId = u.id ?? '';
    this.usernameFromApi = u.username?.trim() ?? '';
    this.userStatusFromApi = u.status != null ? String(u.status).trim() : '';
    this.personalForm.firstName = u.firstName?.trim() ?? '';
    this.personalForm.lastName = u.lastName?.trim() ?? '';
    this.personalForm.email = u.email?.trim() ?? '';

    this.resolvedBureauType = this.parseBureauType(b?.bureauType);
    const uicUser = u.bureauUIC != null && String(u.bureauUIC).trim() !== '' ? String(u.bureauUIC).trim() : '';
    const uicBureau =
      b?.unifiedIdentificationCode != null && String(b.unifiedIdentificationCode).trim() !== ''
        ? String(b.unifiedIdentificationCode).trim()
        : '';
    this.bureauUicForSave = uicUser || uicBureau;

    this.soloBureauNameForSave = b?.name != null ? String(b.name).trim() : '';
    this.soloBarEgnCombined = this.formatSoloBarEgn(b);

    const firmName = b?.name?.trim() ?? '';
    this.firmName = firmName;
    this.firmUicDisplay = uicBureau || uicUser;
    this.firmBarNumberDisplay =
      b?.attorneyNumber != null && String(b.attorneyNumber).trim() !== ''
        ? String(b.attorneyNumber).trim()
        : '';
    this.firmAddressLine = this.formatBureauOfficeLine(b?.address);
    this.firmRepresentatives =
      b?.representatives != null && String(b.representatives).trim() !== ''
        ? String(b.representatives).trim()
        : '';
    this.firmLogoInitials = this.initialsForFirmName(firmName);

    this.roleRaw = u.role != null ? String(u.role).trim() : null;
    this.syncRoleLabel();
    const url = d.profilePictureUrl?.trim();
    this.avatarImageUrl = url ? url : null;
    this.syncAvatarInitials();
    this.ensureValidActiveSection();
    this.profileResponseCache = {
      user: d.user,
      bureau: { ...(d.bureau ?? {}) },
      profilePictureUrl: d.profilePictureUrl,
    };
    if (this.teamUsersFetched) {
      this.rebuildTeamCards();
    }
  }

  /** If firm tab is not available (non-bureau), fall back to personal. */
  private ensureValidActiveSection(): void {
    if (this.activeSection === 'firm' && !this.showFirmSection) {
      this.activeSection = 'personal';
    }
  }

  /** Same display as before: `num / egn` if both, else whichever is set. */
  private formatSoloBarEgn(b: CurrentUserBureauDto | undefined): string {
    if (!b) {
      return '';
    }
    const num = b.attorneyNumber != null ? String(b.attorneyNumber).trim() : '';
    const egn = b.egn != null ? String(b.egn).trim() : '';
    if (num && egn) {
      return `${num} / ${egn}`;
    }
    return num || egn;
  }

  /**
   * Inverse of `formatSoloBarEgn`: `a / b` → two parts; one token → 10 digits = EGN, else bar number.
   */
  private parseSoloBarEgnInput(raw: string): { attorney: string; egn: string } {
    const t = raw.trim();
    if (!t) {
      return { attorney: '', egn: '' };
    }
    const slash = t.split(/\s*\/\s*/);
    if (slash.length >= 2) {
      const attorney = slash[0].trim();
      const egn = slash.slice(1).join(' / ').trim();
      return { attorney, egn };
    }
    if (/^\d{10}$/.test(t)) {
      return { attorney: '', egn: t };
    }
    return { attorney: t, egn: '' };
  }

  private parseBureauType(raw: string | undefined | null): BureauType | null {
    if (raw == null || String(raw).trim() === '') {
      return null;
    }
    const s = String(raw).trim();
    if (s === BureauType.BUREAU) {
      return BureauType.BUREAU;
    }
    if (s === BureauType.SOLO_PRACTICE) {
      return BureauType.SOLO_PRACTICE;
    }
    return null;
  }

  private initialsForFirmName(name: string): string {
    const trimmed = name.trim();
    if (!trimmed) {
      return '—';
    }
    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    if (words[0].length >= 2) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return words[0][0].toUpperCase();
  }

  private syncRoleLabel(): void {
    const key = this.mapApiRoleToI18nKey(this.roleRaw);
    this.personalForm.position = key ? this.translate.instant(key) : (this.roleRaw ?? '');
  }

  private mapApiRoleToI18nKey(role: string | null | undefined): string | null {
    if (!role) return null;
    switch (role) {
      case UserRole.EMPLOYEE:
      case 'ROLE_EMPLOYEE':
        return CONSTANTS.PROFILE_SETTINGS_ROLE_EMPLOYEE;
      case UserRole.OFFICE_MANAGER:
      case 'ROLE_OFFICE_MANAGER':
        return CONSTANTS.PROFILE_SETTINGS_ROLE_OFFICE_MANAGER;
      default:
        return null;
    }
  }

  /** Prefer `adress.adress`, then `adress.address`, then city/postal/country parts. */
  private formatBureauOfficeLine(addr: BureauAddressDto | undefined): string {
    if (!addr) return '';
    const nested = addr.adress;
    if (nested != null && String(nested).trim() !== '') {
      return String(nested).trim();
    }
    const street = addr.address;
    if (street != null && String(street).trim() !== '') {
      return String(street).trim();
    }
    const parts = [addr.postalCode, addr.city, addr.country]
      .filter((p) => p != null && String(p).trim() !== '')
      .map((p) => String(p).trim());
    return parts.join(', ');
  }

  private syncAvatarInitials(): void {
    const f = this.personalForm.firstName.trim();
    const l = this.personalForm.lastName.trim();
    if (f && l) {
      this.avatarInitials = f[0] + l[0];
    } else if (f || l) {
      const single = f || l;
      this.avatarInitials = single.length >= 2 ? single.slice(0, 2) : single;
    } else {
      this.avatarInitials = '—';
    }
  }
}
